import { useId, useState } from 'react'
import classNames from 'classnames'
import { Check, Search, X } from 'lucide-react'
import { Input } from '@/components/input'
import type { LookupItem } from '@/types/lookup'
import styles from './style.module.css'

interface LookupSelectProps {
  id: string
  termo: string
  onTermoChange: (termo: string) => void
  itens: LookupItem[]
  selecionado: LookupItem | null
  disabled?: boolean
  onSelecionar: (item: LookupItem | null) => void
  placeholder?: string
  isLoading?: boolean
  mostrarOpcoesSemBusca?: boolean
}

const LookupSelect = ({
  disabled = false,
  id,
  isLoading = false,
  itens,
  mostrarOpcoesSemBusca = false,
  onSelecionar,
  onTermoChange,
  placeholder = 'Preencha o termo de busca',
  selecionado,
  termo,
}: LookupSelectProps) => {
  const [aberto, setAberto] = useState(false)
  const listaId = useId()

  const limpar = () => {
    onSelecionar(null)
    onTermoChange('')
    setAberto(false)
  }

  const escolher = (item: LookupItem) => {
    onSelecionar(item)
    setAberto(false)
  }

  if (selecionado) {
    return (
      <div className={styles.escolhido}>
        <span className={styles.escolhidoRotulo}>{selecionado.rotulo}</span>
        <button
          type="button"
          className={styles.limpar}
          onClick={limpar}
          disabled={disabled}
          aria-label="Limpar seleção"
        >
          <X size={14} />
        </button>
      </div>
    )
  }

  const mostrarLista = aberto && (termo.trim() !== '' || mostrarOpcoesSemBusca)

  return (
    <div className={styles.campo}>
      <Input
        id={id}
        value={termo}
        placeholder={placeholder}
        autoComplete="off"
        disabled={disabled}
        role="combobox"
        aria-expanded={aberto}
        aria-controls={listaId}
        iconLeft={<Search size={15} />}
        onChange={(event) => {
          onTermoChange(event.target.value)
          setAberto(true)
        }}
        onFocus={() => setAberto(true)}
      />

      {mostrarLista ? (
        <ul className={styles.lista} id={listaId} role="listbox">
          {isLoading ? <li className={styles.aviso}>Buscando</li> : null}

          {!isLoading && itens.length === 0 ? (
            <li className={styles.aviso}>
              {termo.trim() !== '' ? 'Nenhum resultado para a busca' : 'Nenhum item disponível'}
            </li>
          ) : null}

          {itens.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                role="option"
                aria-selected={false}
                className={classNames(styles.opcao)}
                onClick={() => escolher(item)}
              >
                <Check size={14} className={styles.marca} aria-hidden="true" />
                <span className={styles.rotulo}>{item.rotulo}</span>
                {item.detalhe ? <span className={styles.detalhe}>{item.detalhe}</span> : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

export default LookupSelect
